/**
 * SalesCalendarView
 * Core calendar wrapping react-big-calendar with month view,
 * custom event styling by rep color, and click handling.
 */

import React, { useMemo, useCallback } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek as dfStartOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import type {
  CalendarEvent,
  AvailabilityType,
} from "../../types/salesCalendar.types";
import { AVAILABILITY_TYPE_COLORS } from "../../types/salesCalendar.types";

const locales = { "en-US": enUS };

// Wrap startOfWeek to start on Monday (1) instead of Sunday (0)
const startOfWeek = (date: Date) => dfStartOfWeek(date, { weekStartsOn: 1 });

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface SalesCalendarViewProps {
  events: CalendarEvent[];
  currentDate: Date;
  currentView: string;
  onNavigate: (date: Date) => void;
  onView: (view: string) => void;
  onSelectEvent?: (event: CalendarEvent) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
}

const SalesCalendarView: React.FC<SalesCalendarViewProps> = ({
  events,
  currentDate,
  currentView,
  onNavigate,
  onView,
  onSelectEvent,
  onSelectSlot,
}) => {
  // Custom event style: color by rep, dashed border for availability
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    const { type, repColor, availabilityType } = event.resource;

    if (type === "availability") {
      const avColor =
        AVAILABILITY_TYPE_COLORS[availabilityType as AvailabilityType] ||
        repColor;
      return {
        style: {
          backgroundColor: avColor + "20",
          border: "none",
          borderLeft: `3px solid ${avColor}`,
          color: avColor,
          borderRadius: "2px",
          fontSize: "11px",
          padding: "1px 4px",
        },
      };
    }

    if (type === "calendar_request") {
      return {
        style: {
          backgroundColor: repColor + "15",
          border: "none",
          borderLeft: `3px dashed ${repColor}`,
          color: repColor,
          borderRadius: "2px",
          fontSize: "11px",
          padding: "1px 4px",
        },
      };
    }

    // Default: event assignment
    return {
      style: {
        backgroundColor: repColor + "20",
        border: "none",
        borderLeft: `3px solid ${repColor}`,
        color: repColor,
        borderRadius: "2px",
        fontSize: "11px",
        fontWeight: 500,
        padding: "1px 4px",
      },
    };
  }, []);

  // Custom event component showing title and rep name
  const EventComponent = useMemo(
    () =>
      ({ event }: { event: CalendarEvent }) => {
        const { repName, type } = event.resource;
        const prefix = type === "calendar_request" ? "[CR] " : "";
        return (
          <div
            className="truncate"
            title={repName ? `${repName}: ${event.title}` : event.title}
          >
            <span className="font-medium">
              {prefix}
              {event.title}
            </span>
            {repName ? (
              <span className="opacity-70 ml-1">- {repName}</span>
            ) : null}
          </div>
        );
      },
    [],
  );

  return (
    <div className="flex-1 min-h-0">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        allDayAccessor="allDay"
        date={currentDate}
        onNavigate={onNavigate}
        view={currentView as any}
        onView={onView}
        views={[Views.MONTH, Views.WEEK, Views.DAY]}
        eventPropGetter={eventStyleGetter}
        components={{
          event: EventComponent as any,
        }}
        onSelectEvent={onSelectEvent}
        selectable
        onSelectSlot={onSelectSlot}
        popup
        style={{ height: "100%" }}
      />
    </div>
  );
};

export default SalesCalendarView;
